import { useMemo } from 'react';
import { useListSprints, useSprintProductReportsBySprint } from '@data/hooks/sprints';
import { useOrganizationId } from '@app/utils/hooks';

import { ACTIVE_SPRINTS, activeMemberIds } from './activeTeam';
import { useSettledSprints } from './useSettledSprints';

/** The Active team card's people, counted over the last closed sprints that started inside the span. */
export const useActiveMembers = (
  productId: string,
  span: { from: string; to: string } | undefined,
): { active: Set<string>; isLoading: boolean; isError: boolean } => {
  const { data, isLoading: sprintsLoading, isError: sprintsError } = useListSprints({ limit: 400 });
  const settled = useSettledSprints(useOrganizationId(), productId);
  const recent = useMemo(
    () =>
      (data?.items ?? [])
        .filter(sprint => {
          const start = sprint.startDate.slice(0, 10);
          const inSpan = !span || (start >= span.from.slice(0, 10) && start <= span.to.slice(0, 10));
          return inSpan && settled.closedSprintIds.includes(sprint.id);
        })
        .sort((a, b) => a.startDate.localeCompare(b.startDate))
        .slice(-ACTIVE_SPRINTS),
    [data, span, settled.closedSprintIds],
  );
  const reports = useSprintProductReportsBySprint(recent, productId);
  const active = useMemo(() => activeMemberIds(recent, reports.bySprint), [recent, reports.bySprint]);
  return {
    active,
    isLoading: sprintsLoading || settled.isLoading || reports.isLoading,
    isError: sprintsError || settled.isError || reports.isError,
  };
};
